import { Box, Stack, Typography, styled, Tooltip, tooltipClasses, TooltipProps } from "@mui/material";
import { t } from "i18next";
import Chart, { useChart } from "src/components/chart";
import { formatNumber } from "src/utils/format-number";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));

export default function ChartSearchesSpend({ monthlyData, currency, lang }: { monthlyData: any[], currency: string, lang: string }) {
    const categories = monthlyData.map(item => item.month);
    const organic = monthlyData.map(item => item.organic_volume || 0);
    const paid = monthlyData.map(item => item.paid_volume || 0);


    const organic_volume = monthlyData.map(i => i.organic_volume || null);
    const paid_volume = monthlyData.map(i => i.paid_volume || null);


    const chartOptions = useChart({
        chart: {
            type: 'bar',
            toolbar: { show: false },
            stacked: true,
        },
        plotOptions: {
            bar: { columnWidth: '20%' },
        },
        fill: { type: ['solid', 'solid'] },
        colors: ['rgba(24, 161, 85, 1)', 'rgba(192, 153, 16, 1)'],
        xaxis: {
            tooltip: { enabled: false },
            type: 'category',
            categories,
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: [
            { show: false, min: 0, labels: { formatter: (v: number) => formatNumber(v) } },
        ],

        grid: { show: false },
        tooltip: {
            shared: true,
            intersect: false,
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const item = monthlyData[dataPointIndex] || { organic_volume: 0, paid_volume: 0 };

                const tooltipData = [
                    { label: t("dashboard.global.brandPulse.organic"), value: item.organic_volume, color: 'rgba(24, 161, 85, 1)' },
                    { label: t("dashboard.global.brandPulse.paid"), value: item.paid_volume, color: 'rgba(192, 153, 16, 1)' },
                ];

                tooltipData.sort((a, b) => b.value - a.value);

                return `
            <div class="visibility-chart-tooltip" style="width: 250px;">
                <div class="visibility-chart-tooltip-header" style="margin-bottom: 8px; font-weight: bold;">
                    <span class="visibility-chart-tooltip-title">${t("dashboard.nonBrand.columns.clicksSplit")}</span>
                    <span class="visibility-chart-tooltip-date" style="float: right;">${item.month}</span>
                </div>
                ${tooltipData.map((d: any) => `
                    <div class="visibility-chart-tooltip-row" style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                        <div class="visibility-chart-tooltip-indicator" style="background-color: ${d.color}"></div>
                        <span class="visibility-chart-tooltip-label">${d.label}</span>
                        <span style="color: rgba(51, 60, 71, 1)">${formatNumber(d.value)}</span>
                    </div>
                `).join('')}
            </div>
        `;
            },
        },
        legend: { show: false },
    });

    const series = [
        { name: "Organic volume", type: 'bar', data: organic_volume },
        { name: "Paid volume", type: 'bar', data: paid_volume },
    ];
    const renderFooter = (title: string) => {
        return (
            <Stack
                width="100%"
                sx={{
                    display: "flex",
                    height: 32,
                    px: "12px",
                    py: "8px",
                    alignItems: "flex-start",
                    alignSelf: "stretch",
                    backgroundColor: "rgba(145, 158, 171, 0.08)",
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                }}
            >
                <Typography
                    fontSize="13px"
                    variant="caption"
                    color="rgba(99, 115, 129, 1)"
                >
                    {title}
                </Typography>
            </Stack>
        );
    };

    return (
        <Box
            bgcolor="white"
            borderRadius={1}
            sx={{
                border: "1px solid rgba(145, 158, 171, 0.12)",
            }}
        >
            <Stack className="card-top-header" borderBottom={1} borderColor="divider" direction="row" spacing={3} py={1} px={2} justifyContent="space-between">
                <LightTooltip title={t('dashboard.global.brandPulse.definitions.organic_vs_paid')}>
                    <Stack className="card-top-header-item" direction="row" alignItems="center" spacing={1} sx={{ cursor: 'help' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography className="card-top-header-item-label" variant="h6" color="text.primary">
                                {t("dashboard.global.brandPulse.organic_vs_paid")}
                            </Typography>
                        </Stack>
                        <Typography className="card-top-header-item-value" variant="h6" color="text.primary">
                            {formatNumber(organic_volume.reduce((a, b) => a + b, 0) + paid_volume.reduce((a, b) => a + b, 0))}
                        </Typography>
                    </Stack>
                </LightTooltip>
            </Stack>

            <Box height={200}>
                <Chart type="bar" series={series} options={chartOptions} height="100%" />
            </Box>

            {renderFooter(t("dashboard.global.brandPulse.last12Months"))}
        </Box>
    );
}
